import type {
  BackupOverview,
  BackupRun,
  RemoteBackupSettings,
  RemoteBackupUpdate,
  RemoteResult,
  RestoreResult,
} from '@panelvault/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api';

const backupsKey = ['backups'] as const;
const remoteKey = ['backups', 'remote'] as const;

export function useBackups() {
  return useQuery({
    queryKey: backupsKey,
    queryFn: () => api.get<BackupOverview>('/backups'),
  });
}

export function useCreateBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<BackupRun>('/backups'),
    // The list and the off-site status (last upload) both change.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: backupsKey }),
  });
}

export function useRestoreBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<RestoreResult>(`/backups/${encodeURIComponent(name)}/restore`),
    // Everything in the app changed.
    onSuccess: () => void queryClient.invalidateQueries(),
  });
}

export function useRemoteBackup() {
  return useQuery({
    queryKey: remoteKey,
    queryFn: () => api.get<RemoteBackupSettings>('/backups/remote'),
  });
}

export function useUpdateRemoteBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: RemoteBackupUpdate) =>
      api.patch<RemoteBackupSettings>('/backups/remote', update),
    onSuccess: (settings) => queryClient.setQueryData(remoteKey, settings),
  });
}

export function useTestRemoteBackup() {
  return useMutation({
    mutationFn: () => api.post<RemoteResult>('/backups/remote/test'),
  });
}

export function useUploadBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<RemoteResult>(`/backups/${encodeURIComponent(name)}/upload`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: remoteKey }),
  });
}
